/**
 * InStoreService Component
 *
 * This component displays a selection of in-store services offered to customers.
 * Each service is represented as a card that includes a title, description,
 * and an image, along with a "BOOK NOW" button for user interaction.
 *
 * Features:
 * - Presents multiple services using a responsive card layout.
 * - Displays images associated with each service to enhance visual appeal.
 * - Provides a button for users to book services directly.
 *
 * Data Structure:
 * - The services are defined in an array of objects, each containing:
 *   - title: A brief title of the service.
 *   - description: A detailed explanation of what the service includes.
 *   - imageUrl: A URL to an image representing the service.
 */

import Button from 'components/commercetools-ui/atoms/button';
import Typography from 'components/commercetools-ui/atoms/typography';
import { FrontasticImage } from 'frontastic/lib/image';
import Link from 'next/link';
import React from 'react';

type CardData = {
  title?: string;
  description?: string;
  imageUrl?: FrontasticImage;
  ctaLink?: string;
  ctaName?: string;
};

interface InStoreServiceProps {
  cardData: CardData[];
}

const InStoreService: React.FC<InStoreServiceProps> = ({ cardData }) => {
  return (
    <section>
      <div className="mb-30 flex justify-center">
        <Typography className="text-center text-[29px] font-bold uppercase leading-[110%] md:text-[36px]">
          Our Instore Services
        </Typography>
      </div>

      <section className="pb-10 lg:pb-20">
        <div className="container mx-auto">
          <div className="flex flex-wrap  justify-between">
            {cardData.map((card, index) => (
              <div key={index} className="flex w-full gap-x-10 p-15 px-4 md:w-1/2 xl:w-[350px]">
                <div className="mb-10 flex h-full flex-col overflow-hidden bg-white duration-300">
                  <img src={card?.imageUrl?.media?.file} alt="image" className="w-full" />
                  <div className="flex flex-grow flex-col justify-between p-8 text-center sm:p-9 md:p-7 xl:p-9">
                    <div className="my-20">
                      {card?.title ? (
                        <h3>
                          <a
                            href="javascript:void(0)"
                            className="text-dark hover:text-primary mb-4 block text-xl font-semibold sm:text-[22px] md:text-xl lg:text-[22px] xl:text-xl 2xl:text-[22px]"
                          >
                            {card?.title}
                          </a>
                        </h3>
                      ) : null}
                      <p className="text-body-color mb-7 text-base leading-relaxed">{card?.description}</p>
                    </div>
                    <Link href={card?.ctaLink ?? ''}>
                      <Button className="mt-22 md:mt-36 md:px-48 md:py-12 lg:mt-32">
                        <Typography as="span" className="text-12 text-black md:text-14 lg:text-16">
                          {card?.ctaName}
                        </Typography>
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </section>
  );
};

export default InStoreService;
